import React, { useState, useEffect, useRef } from 'react'
import { GlobalContext } from './GlobalContext'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useParams,
  useNavigate,
  json,
} from "react-router-dom"

import { v4 as uuidv4 } from 'uuid'
// utilities
import CloudformationOutputs from './CloudformationOutputs.json'
import { checkIfSubscriptionExpiringWithoutPayment } from './utils/checkIfSubscriptionExpiringWithoutPayment'
import { publish } from './utils/pubsub'
// UI Elements
import Footer from './ui-elements/Footer'
import Header from './ui-elements/Header'
// shared components
import AppTermsDialog from './shared-components/AppTermsDialog'
import SubscriptionExpired from './shared-components/SubscriptionExpired'
import SubscriptionExpiring from './shared-components/SubscriptionExpiring'
import NotLoggedIn from './auth/NotLoggedIn'
import Dashboard from './dashboard/Dashboard'
import ChecklistQuestions from './dashboard/ChecklistQuestions'
import QuestionPage from './question-pages/QuestionPage'
import VideoProvider from './context/video/VideoProvider'
import VideoBackground from './shared-components/VideoBackground'
import ScanQrCode from './question-pages/ScanQrCode'
import StartAdhocChecklist from './dashboard/StartChecklist'
import CompleteChecklist from './dashboard/CompleteChecklist'
import AddPhotoNote from './question-pages/AddPhotoNote'
import AllChecklists from './dashboard/AllChecklists'
import OrgMismatchDialog from './shared-components/OrgMismatchDialog'
import AddAnswerToQuestion from './question-pages/AddAnswerToQuestion'
import MessageModal from './ui-elements/MessageModal'
import { url } from 'inspector'
import ProcessSummary from './processes/ProcessSummary'
import ProcessList from './processes/ProcessList'
import ProcessSummaryCheckPeriods from './processes/ProcessSummaryCheckPeriods'
import NoOrganisation from './ui-elements/NoOrganisation'
import { log } from 'console'
import ReconnectModal from './ui-elements/ReconnectModal'
import LoginModal from './ui-elements/LoginModal'
import JsonDebug from './ui-elements/JsonDebug'
import ExternalUserDashboard from './dashboard/ExternalUserDashboard'
import ChecklistCodeScanned from './dashboard/ChecklistCodeScanned'
import VoiceProvider from './context/voice/VoiceProvider'
import VoiceDialog from './shared-components/VoiceDialog'
import OrientationProvider from './context/orientation/OrientationProvider'
import { useTranslation } from 'react-i18next'
import { UserStatusType } from '@aws-sdk/client-cognito-identity-provider'



type Organisations = null | ObjectStringKeyAnyValue

function App() {

  const [loggedIn, setLoggedIn] = useState<boolean | string>(false)
  const [idToken, setIdToken] = useState<string | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [socket, setSocket] = useState<WebSocket | null>(null)
  const [connectionState, setConnectionState] = useState('disconnected')
  const [orgSettings, setOrgSettings] = useState('disconnected')
  const [userData, setUserData] = useState<ObjectStringKeyAnyValue | null>(null)
  const [currentOrganisation, setCurrentOrganisation] = useState<string | null>(null)
  const [orgIdFromQrCode, setOrgIdFromQrCode] = useState<string | null>(null)
  const WebSocketURI = CloudformationOutputs.WebSocketURI
  const [tableData, setTableData] = useState<ObjectStringKeyAnyValue | null>(null)
  const [processData, setProcessData] = useState<ObjectStringKeyAnyValue | null>(null)
  const [loggedInUserRole, setLoggedInUserRole] = useState<string | null>(null)
  const [activeFeatures, setActiveFeatures] = useState<{ [key: string]: any }>({})
  const [expiredFeatures, setExpiredFeatures] = useState<{ [key: string]: any }>({})
  const [isAccountSetup, setIsAccountSetup] = useState<boolean | null>(null)
  const [agreementNeeded, setAgreementNeeded] = useState<{ [key: string]: any } | null>(null)
  const [subscriptionHasBeenChecked, setSubscriptionHasBeenChecked] = useState<boolean>(false)
  const [subscriptionExpired, setSubscriptionExpired] = useState<boolean>(false)
  const [subscriptionExpiringWithoutPayment, setSubscriptionExpiringWithoutPayment] = useState<boolean>(false)
  const [checkPeriods, setCheckPeriods] = useState<ObjectStringKeyAnyValue | null>(null)
  const [questionsFetchedForCheckPeriodIds, setQuestionsFetchedForCheckPeriodIds] = useState<Array<string>>([])
  const [questionsAndAssetsFetchedForChecklists, setQuestionsAndAssetsFetchedForChecklists] = useState<Array<string>>([])
  const [activeChecklistIds, setActiveChecklistIds] = useState<Array<string>>([])
  const [inActiveChecklistIds, setInActiveChecklistIds] = useState<Array<string>>([])
  const [checkPeriodIdsForActiveCheckists, setCheckPeriodIdsForActiveChecklists] = useState<any>({})
  const [isVideoMode, setIsVideoMode] = useState<boolean>(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false)
  const [isInPhoneMode, setIsInPhoneMode] = useState(true)
  const [loginOptions, setLoginOptions] = useState<null | ObjectStringKeyAnyValue>(null)
  const [isSignoffRequired, setIsSignoffRequired] = useState(false)
  const [orgMismatchDialog, setOrgMismatchDialog] = useState<string | null>(null)
  const [showModal, setShowModal] = useState<ObjectStringKeyAnyValue | null>(null)
  const [showReconnectModal, setShowReconnectModal] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [assetIdsToFetch, setAssetIdsToFetch] = useState<Array<string>>([])
  const [externalUserState, setExternalUserState] = useState<null | ObjectStringKeyAnyValue>(null)
  const [voiceModalTimeoutTimer, setVoiceModalTimeoutTimer] = useState<NodeJS.Timeout | null>(null)
  const orgIdFromQrCodeRef = useRef(orgIdFromQrCode)
  const loginOptionsRef = useRef(loginOptions)
  // const [assetIdsToFetch, setAssetIdsToFetch] = useState<string[]>([])
  const lastKnownProcessDefinitionValueRef = useRef<any>(null)
  const lastKnownProcessRecordsValueRef = useRef<any>(null)
  const lastKnownCheckPeriodsValueRef = useRef<any>(null)
  const socketRef = useRef<WebSocket | null>(null)

  // i18n translation
  const { t } = useTranslation()
  const { i18n } = useTranslation()
  const RTLLanguages = ['ar', 'he']
  const isRTL = RTLLanguages.includes(i18n.language)
  const isLocalisationEnabled = tableData && tableData.Organisation && tableData.Organisation.Options && tableData.Organisation.Options.localisation && tableData.Organisation.Options.localisation === true ? true : false


  // console.log(tableData)

  // const id = uuidv4()
  // console.log(id)


  // tableData && tableData.Assets && console.log(tableData.Assets)
  // tableData && tableData.ChecklistQuestions && console.log(JSON.stringify(tableData.ChecklistQuestions))

  // To do - postpone this until after we're sure we're not about to be sent to a cognito login screen


  useEffect(() => {
    if (!isLocalisationEnabled) {
      //i18n.changeLanguage('en')
    }
  }, [isLocalisationEnabled])

  useEffect(() => {
    checkForToken()
    setConnectionState('tryToConnect')
  }, [])



  useEffect(() => {
    // set the socket ref to the current socket
    if (socket) {
      socketRef.current = socket
    } else {
      socketRef.current = null
    }
  }, [socket])

  useEffect(() => {
    orgIdFromQrCodeRef.current = orgIdFromQrCode
  }, [orgIdFromQrCode])

  useEffect(() => {
    loginOptionsRef.current = loginOptions
  }, [loginOptions])


  useEffect(() => {
    // tesitng functionalty to test websocket reconnection
    // listen for hotkey press ctrl + 1.  if pressed force socket to close 
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === '1') {
        console.log('🔌  forcing socket to close')
        if (socket) {
          socket.close()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [socket])




  useEffect(() => {
    if (showModal && (showModal['heardVoiceAnswer'] || showModal['heardVoiceCommand'])) {
      if (voiceModalTimeoutTimer) {
        clearTimeout(voiceModalTimeoutTimer)
      }
      const timer = setTimeout(() => {
        setShowModal(null)
      }, 2500)
      setVoiceModalTimeoutTimer(timer)

      return () => clearTimeout(timer);
    }
  }, [showModal])



  useEffect(() => {
    switch (connectionState) {
      case 'closed':
      case 'error':
        setShowReconnectModal(true)
        setConnectionState('waitThenReconnect')
        setTimeout(() => {
          setConnectionState('tryToConnect')
        }, 200)
        break;
      case 'tryToConnect':
        setShowReconnectModal(true)
        setConnectionState('connecting')
        if (!socket) {
          const newSocket = new WebSocket(WebSocketURI)
          //console.log("🔌  opening websocket...")
          newSocket.onopen = () => {
            setSocket(newSocket)
            //console.log("🔌  socket has been opened")
          }
          newSocket.onclose = () => {
            //console.log("🔌  socket closed")
            setConnectionState('closed')
            setSocket(null)
          }
          newSocket.onerror = () => {
            setConnectionState('error')
            console.log("🔌 socket error")
            setSocket(null)
          }
        } else {
          //console.log("🔌  socket already exists, not creating a new one")
        }
        break

      case 'connecting':
        // it may be that the access tokens are in local storage but not in state variables,
        // we need to check and set them if neccessary

        if (!idToken && localStorage.getItem('localIdToken')) {
          setIdToken(localStorage.getItem('localIdToken'))
        }
        if (!accessToken && localStorage.getItem('localAccessToken')) {
          setAccessToken(localStorage.getItem('localAccessToken'))
        }
        if (!refreshToken && localStorage.getItem('localRefreshToken')) {
          setRefreshToken(localStorage.getItem('localRefreshToken'))
        }

        if (socket) {
          setConnectionState('connected-awaiting-login')
        }
        break

      case 'connected-awaiting-login':
        setShowReconnectModal(true)
        findLoginsPermittedForChecklistId()
        checkForGuestConnection()
        //console.log("🔌  connected and awaiting login")
        //console.log(socket ? 'socket exists' : 'socket does not exist')
        break

      case 'connected-and-logged-in':
        setShowReconnectModal(false)
        fetchData()
        //console.log("🔌  connected and logged in")
        break

    }

  }, [connectionState, socket])



  useEffect(() => {
    if (
      tableData && tableData.Organisation &&
      tableData.Organisation.ChecklistOptions &&
      tableData.Organisation.ChecklistOptions.RequireSignoff === true
    ) {
      setIsSignoffRequired(true)
    } else {
      setIsSignoffRequired(false)
    }
    // checkForAssetsToFetch()
    // updateAssetIdsToFetch()
  }, [tableData])



  function updateProcessData(tableData: ObjectStringKeyAnyValue) {
    //console.log("🍉 Updating process data")
    const newProcessData: ObjectStringKeyAnyValue = {}

    // iterate through process definitions and add to process data object
    if (tableData.ProcessDefinitions) {
      for (const processId in tableData.ProcessDefinitions) {
        const processDefinition = tableData.ProcessDefinitions[processId]
        //@ts-ignore
        newProcessData[processId] = {
          DisplayName: processDefinition.DisplayName,
          InProgress: false,
          Description: 'Ready to start',
          Stages: processDefinition.Stages
        }
      }
    }

    if (tableData.ProcessRecords) {
      // console.log("There are process records")
      // console.log(tableData.ProcessRecords)
      // iterate through process records, if the process record is active set newProcessData[processId] to active for the corresponding process and add the active record id
      for (const processRecordId in tableData.ProcessRecords) {
        const processRecord = tableData.ProcessRecords[processRecordId]
        // console.log(`👀 looking at process record ${processRecordId}`)
        // console.log(processRecord)

        if (processRecord.InProgress) {
          //console.log("Process is in progress")
          const process_id_for_record = processRecord.ProcessDefinitionId as string
          if (process_id_for_record) {
            const currentStageId = processRecord.CurrentStageId
            const currentStage = tableData && tableData.ProcessDefinitions && tableData.ProcessDefinitions[process_id_for_record] && tableData.ProcessDefinitions[process_id_for_record].Stages[currentStageId]
            const currentStageName = currentStage && currentStage.DisplayName

            newProcessData[process_id_for_record] ||= {}
            if (currentStage && currentStage.NextSteps) {
              newProcessData[process_id_for_record]['NextSteps'] = currentStage.NextSteps
            }
            newProcessData[process_id_for_record]['InProgress'] = true
            newProcessData[process_id_for_record]['processRecord'] = processRecord
            newProcessData[process_id_for_record]['Description'] = `Stage: ${currentStageName}`
            // 
            // thisProcessData.processRecord.StageProgress[currentStageId] && thisProcessData.processRecord.StageProgress[currentStageId].ChecklistPeriodIds
            //console.log("looking for check periods for this stage")
            const processRecordForCurrentStage = processRecord && processRecord.StageProgress && processRecord.StageProgress[currentStageId]

            newProcessData[process_id_for_record]['PendingCheckPeriodIds'] = []
            newProcessData[process_id_for_record]['PendingCheckPeriods'] = []

            newProcessData[process_id_for_record]['CompletedCheckPeriodIds'] = []
            newProcessData[process_id_for_record]['CompletedCheckPeriods'] = []


            if (processRecordForCurrentStage && processRecordForCurrentStage.ChecklistPeriodIds) {
              //console.log("Found checklist periods")
              for (const checklistPeriodId of processRecordForCurrentStage.ChecklistPeriodIds) {
                //console.log(`Looking for checklist period ${checklistPeriodId}`)
                const thisCheckPeriod = tableData && tableData.ChecklistPeriods && tableData.ChecklistPeriods[checklistPeriodId]
                if (thisCheckPeriod) {
                  if (thisCheckPeriod.Progress && thisCheckPeriod.Progress.progress_percentage == 100) {
                    //console.log("Found a checklist period")
                    //console.log(thisCheckPeriod.Progress)
                    newProcessData[process_id_for_record]['CompletedCheckPeriodIds'].push(checklistPeriodId)
                    newProcessData[process_id_for_record]['CompletedCheckPeriods'].push(thisCheckPeriod)
                  } else {
                    newProcessData[process_id_for_record]['PendingCheckPeriodIds'].push(checklistPeriodId)
                    newProcessData[process_id_for_record]['PendingCheckPeriods'].push(thisCheckPeriod)
                  }
                }
              }
            }
            //@ts-ignore


          }
        }
      }
    } else {
      //    console.log("No process records found")
      //    console.log(Object.keys(tableData))
    }

    setProcessData(newProcessData)
  }


  useEffect(() => {
    if (!tableData) {
      return
    }
    // see if changes have been made to tableData['Processes'] or tableData['ProcessRecords']
    // use refs to compare the previous value to the current value
    if (tableData && (tableData.ProcessDefinitions || tableData.ProcessRecords)) {
      if (lastKnownProcessDefinitionValueRef.current !== tableData.ProcessDefinitions || lastKnownProcessRecordsValueRef.current !== tableData.ProcessRecords || lastKnownCheckPeriodsValueRef.current !== tableData.ChecklistPeriods) {
        lastKnownProcessDefinitionValueRef.current = tableData.Processes
        lastKnownProcessRecordsValueRef.current = tableData.ProcessRecords
        lastKnownCheckPeriodsValueRef.current = tableData.ChecklistPeriods
        updateProcessData(tableData)

      }
    } else {
      // console.log("no changes to process data")
    }
  }, [tableData])

  useEffect(() => {
    if (tableData) {
      checkIfSubscriptionExpiringWithoutPayment(tableData, subscriptionHasBeenChecked, setSubscriptionExpiringWithoutPayment, setSubscriptionHasBeenChecked)
      // set logged in user role
      if (tableData.Users && userData) {
        const loggedInUserId = userData['cognito:username']
        const role = tableData.Users[loggedInUserId] ? tableData.Users[loggedInUserId].Role : null
        setLoggedInUserRole(role)
      }
    }
  }, [tableData, userData, checkPeriods])

  useEffect(() => {
    // handle incoming messages
    if (socket !== null) {
      actOnMessage()
    }
  }, [socket])

  useEffect(() => {
    handleTokens()
  }, [idToken, accessToken, connectionState, loggedIn, currentOrganisation, refreshToken])


  useEffect(() => {
    if (tableData && tableData.ChecklistPeriods) {
      const myGuestLoginInfo = localStorage.getItem('guestLogin')
      const myGuestLoginId = myGuestLoginInfo ? JSON.parse(myGuestLoginInfo).Id : null
      if (myGuestLoginId) {
        for (const checkPeriodId in tableData.ChecklistPeriods) {
          const thisCheckPeriod = tableData.ChecklistPeriods[checkPeriodId]
          const startedByUserDetails = thisCheckPeriod.startedByUserDetails
          const startedByUserGuestId = startedByUserDetails ? startedByUserDetails.GuestConnectionId : null
          const startedByCurrentUser = startedByUserGuestId && startedByUserGuestId === myGuestLoginId ? true : false
          if (startedByCurrentUser) {
            console.log('🪼 Found check period', thisCheckPeriod.Id, 'started by guest id', startedByUserGuestId)
            if (externalUserState && !externalUserState.checklistPeriodId) {
              setExternalUserState((prevState: any) => ({
                ...prevState,
                checklistPeriodId: thisCheckPeriod.Id
              }))

              setShowModal(null)

              // jump straight to questions
              window.location.href = `/checklists/${thisCheckPeriod.Id}`

            } else {
              console.log('Criteria not met to jump to questions for guest user')
              console.log(externalUserState)
              if (externalUserState && externalUserState.checklistPeriodId != thisCheckPeriod.Id) {
                console.log('❌ Checklist period id does not match stored state')
                if (thisCheckPeriod.Id) {
                  setExternalUserState((prevState: any) => ({
                    ...prevState,
                    checklistPeriodId: thisCheckPeriod.Id
                  }))


                  // jump straight to questions
                  window.location.href = `/checklists/${thisCheckPeriod.Id}`


                }
              }
            }
          }
        }


      } else {
        //console.log('❌ No guest login info found in local storage')
      }

    }

  }, [tableData, externalUserState])



  const handleTokens = async () => {


    if (connectionState == 'connected-awaiting-login') {
      if (idToken && accessToken && refreshToken) {
        //console.log("Handling tokens", refreshToken)
        const tokensExpired = checkIfTokensExpired(accessToken, idToken, refreshToken)
        if (tokensExpired) {
          setLoggedIn(false)
          //console.log("Tokens expired, refreshing...")
          const newTokens = await fetchRefreshedTokens(refreshToken)
          if (newTokens && newTokens.id_token && newTokens.access_token) {
            //console.log("🎱 New tokens received")
            setIdToken(newTokens.id_token)
            setAccessToken(newTokens.access_token)
            localStorage.setItem('localIdToken', newTokens.id_token)
            localStorage.setItem('localAccessToken', newTokens.access_token)
            localStorage.setItem('localRefreshToken', newTokens.refresh_token)
            sendTokensToWebsocket(newTokens.access_token, newTokens.id_token, newTokens.refresh_token)
          } else {
            console.log("Error: Failed to refresh tokens")
            console.log(newTokens)
            console.log('🚨')
            handleLogout()
          }
        } else {
          //console.log("Tokens are still valid")
          if (accessToken != null && idToken && refreshToken) {
            sendTokensToWebsocket(accessToken as string, idToken as string, refreshToken as string)
          }
        }
      } else {
        // check if there is a guest login token we can try
        const localGuestLogin = localStorage.getItem('guestLogin')
        if (!localGuestLogin && loggedIn) {
          //console.log('🐝')
          handleLogout()
        }
      }
    }

  }

  const getUserStats = () => {
    console.log('📊 User Stats:')
    const userStats: ObjectStringKeyAnyValue = {}
    userStats['deviceDetails'] = navigator.userAgent
    userStats['language'] = navigator.language
    return userStats
  }

  const sendTokensToWebsocket = async (accessToken: string, idToken: string, refreshToken: string) => {
    //console.log("Sending tokens to websocket")
    const payloadObj: ObjectStringKeyAnyValue = {
      action: "login",
      loggingInFromApp: "mobilechecklist",
      idToken: idToken,
      accessToken: accessToken,
      refreshToken: refreshToken,
      userStats: getUserStats()
    }
    const selectedOrgId = localStorage.getItem("selectedOrgId")
    if (selectedOrgId) {
      payloadObj['selectedOrgId'] = selectedOrgId
    }
    const payload = JSON.stringify(payloadObj)
    sendMessageToWebsocket(payload)
  }

  const checkIfTokensExpired = (accessToken: string, idToken: string, refreshToken: string) => {

    try {
      const tokenExpiry = new Date(JSON.parse(atob(idToken.split('.')[1])).exp * 1000)
      const currentTime = new Date()
      const timeUntilExpiry = tokenExpiry.getTime() - currentTime.getTime()
      const refreshIfRemainingTimeIsLessThan = 5 * 60 * 1000 // 5 minutes in milliseconds
      //console.log(`Time until expiry: ${timeUntilExpiry / 60000}mins. Time limit: ${refreshIfRemainingTimeIsLessThan / 60000}mins`)
      if (timeUntilExpiry < refreshIfRemainingTimeIsLessThan) {
        return true
      }
      else {
        return false
      }
    } catch (error) {
      console.error('Error checking token expiry:', error)
      return false
    }
  }


  const fetchRefreshedTokens = async (refreshToken: string) => {
    //console.log("Fetching refreshed tokens for token: ", refreshToken)
    const clientId = CloudformationOutputs.UserPoolClientId
    const userPoolDomain = CloudformationOutputs.UserPoolDomain
    const awsRegion = CloudformationOutputs.AWSRegion
    const url = `https://${userPoolDomain}.auth.${awsRegion}.amazoncognito.com/oauth2/token`

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: refreshToken
    })

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(`Error refreshing token: ${error.error_description}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to refresh token:', error)
      return null
    }
  }


  const redirectAfterLogin = () => {
    const lastVisitedPage = localStorage.getItem('lastVistedPage')
    if (lastVisitedPage) {
      console.log(`➡️ redirecting to ${lastVisitedPage}`)
      localStorage.removeItem('lastVistedPage')
      window.location.href = lastVisitedPage
    }
  }


  const checkForToken = () => {
    //console.log('checking for a token')
    const localIdToken = localStorage.getItem("localIdToken")
    const localAccessToken = localStorage.getItem("localAccessToken")
    const localRefreshToken = localStorage.getItem("localRefreshToken")
    const queryStringParams = window.location.hash.replace("#", "").split(/[&=]+/)

    let idToken = null
    let accessToken = null
    let refreshToken = null

    //console.log(`query string params: ${queryStringParams}`)

    const indexOfIdToken = queryStringParams.indexOf('id_token')
    if (indexOfIdToken >= 0) {
      //console.log(`id token found at index ${indexOfIdToken}`)
      idToken = queryStringParams[indexOfIdToken + 1]
      //console.log(`🎱 id token found: ${idToken}`)
    } else {
      //console.log(`id token not found in query string`)
    }

    const indexOfAccessToken = queryStringParams.indexOf('access_token')
    if (indexOfAccessToken >= 0) {
      //console.log(`access token found at index ${indexOfAccessToken}`)
      accessToken = queryStringParams[indexOfAccessToken + 1]
      console.log(`🥎 access token found: ${accessToken}`)
    }


    const indexOfRefreshToken = queryStringParams.indexOf('refresh_token')
    if (indexOfRefreshToken >= 0) {
      refreshToken = queryStringParams[indexOfRefreshToken + 1]
      console.log(`🏀 refresh token found: ${refreshToken}`)
    }


    if (idToken && accessToken && refreshToken) {
      console.log('login pending')
      setLoggedIn('pending')

      if (localStorage.getItem('simulatedPhoneLogin') === 'true') {
        window.location.href = '/phonemode' + window.location.search
      } else {
        //console.log("Not a phone mode login")
      }

      setIdToken(idToken)
      setAccessToken(accessToken)
      setRefreshToken(refreshToken)
      localStorage.setItem('localIdToken', idToken)
      localStorage.setItem('localAccessToken', accessToken)
      localStorage.setItem('localRefreshToken', refreshToken)
      redirectAfterLogin()

    } else if (localIdToken && localAccessToken && localRefreshToken) {
      //console.log('logged in earlier')
      setIdToken(localIdToken)
      setAccessToken(localAccessToken)
      setRefreshToken(localRefreshToken)
    } else {
      //console.log('not logged in')
      //window.location.href = loginUrl
    }
  }



  const findLoginsPermittedForChecklistId = () => {
    // console.log("Finding login options permitted for checklist id from url")
    const pathName = window.location.pathname
    const pathNameArray = pathName.split('/')
    const url_params = new URLSearchParams(window.location.search)
    let asset_id = url_params.get('assetId')
    let process_id = null
    let qrcode = url_params.get('qrcode')
    let urlType = null

    if (
      pathNameArray.length >= 3 && (pathNameArray[1] === 'start-checklist')) {
      urlType = 'start_adhoc'
    }

    if (
      pathNameArray.length >= 3 && (pathNameArray[1] === 'checklist-qr-scanned')) {
      urlType = 'start_adhoc'
    }


    if (
      pathNameArray.length >= 3 && (pathNameArray[1] === 'checklists')) {
      urlType = 'start_cp'
    }


    if (pathNameArray.length >= 3 && pathNameArray[1] === 'start-checklist-for-asset') {
      urlType = 'start_adhoc_for_asset'
      asset_id = pathNameArray[3]
    }

    if (pathNameArray.length >= 3 && pathNameArray[1] === 'checklist-qr-scanned-for-asset') {
      console.log("Detected checklist-qr-scanned-for-asset url")
      urlType = 'start_adhoc_for_asset'
      asset_id = pathNameArray[3]
    }



    if (pathNameArray.length >= 2 && pathNameArray[1] == 'process') {
      urlType = 'process'
      process_id = pathNameArray[1]
    }

    //  console.log(`Pathname array length is ${pathNameArray.length} [${pathNameArray[0]}] [${pathNameArray[1]}]`)
    if (pathNameArray.length == 2 && pathNameArray[0] === '' && pathNameArray[1].length === 5) {
      urlType = 'asset_sticker'
    }

    // console.log(`Url type is ${urlType}`)
    if (urlType === 'start_adhoc' || urlType === 'start_adhoc_for_asset' || urlType === 'checklist-qr-scanned') {
      const checklistIdFromPath = pathNameArray[2]
      // console.log(`checking login options for checklist id ${checklistIdFromPath}`)
      const payloadObj: ObjectStringKeyAnyValue = {
        action: "guestLogin",
        guestLoginAction: "findLoginsPermittedForChecklistId",
        checklistId: `${checklistIdFromPath}`,
        userStats: getUserStats()
      }
      //console.log(payloadObj)
      if (asset_id) {
        payloadObj['asset_id'] = asset_id
      }
      const payload = JSON.stringify(payloadObj)
      sendMessageToWebsocket(payload)
    } else if (urlType === 'start_cp') {
      // get sticker pack id from end of url eg ?stickerId=G81V
      const stickerId = url_params.get('assetId') || url_params.get('stickerId')
      //console.log("🍍 checking login options for checklist period")
      if (stickerId && stickerId.length > 0) {
        const payloadObj: ObjectStringKeyAnyValue = {
          action: "guestLogin",
          'guestLoginAction': 'requestLoginActionForSticker',
          'stickerId': stickerId,
          userStats: getUserStats()
        }
        const payload = JSON.stringify(payloadObj)
        sendMessageToWebsocket(payload)
      }
    } else if (urlType === 'process' && qrcode) {
      const payloadObj: ObjectStringKeyAnyValue = {
        action: "guestLogin",
        'guestLoginAction': 'requestLoginActionForQRCode',
        'QRCode': qrcode,
        userStats: getUserStats()
      }
      const payload = JSON.stringify(payloadObj)
      sendMessageToWebsocket(payload)
    } else if (urlType === 'asset_sticker') {
      const stickerId = pathNameArray[1]
      const payloadObj: ObjectStringKeyAnyValue = {
        action: "guestLogin",
        'guestLoginAction': 'requestLoginActionForSticker',
        'stickerId': stickerId,
        userStats: getUserStats()
      }
      const payload = JSON.stringify(payloadObj)
      sendMessageToWebsocket(payload)

    } else {
      //console.log("This doesn't look like a url where I can find the login options")
    }
  }


  const checkForGuestConnection = () => {
    const localGuestLogin = localStorage.getItem('guestLogin')
    if (localGuestLogin) {
      //console.log('👤 guest login details found in local storage')
      const payload = JSON.parse(localGuestLogin)
      payload['action'] = "guestLogin"
      payload['guestLoginAction'] = "guestLoginRequestFromLocalStorage"
      payload['userStats'] = getUserStats()
      sendMessageToWebsocket(JSON.stringify(payload))
    } else {
      //console.log('⛔️ no guest login details found in local storage')
    }
  }

  const switchCurrentOrganisation = (organisationId: string, redirect = true, reopenSocket = true) => {
    console.log(`👮 Organization id change requested from ${currentOrganisation} to ${organisationId}`)
    localStorage.setItem("selectedOrgId", organisationId)
    setCurrentOrganisation(organisationId)
    setTableData(null)

    if (reopenSocket) {
      socket && socket.close()
    }

    if (redirect) {
      window.location.href = '/'
    }
  }


  const sendMessageToWebsocketOrThrowException = async (payload: string) => {
    if (socket) {
      if (!socket.readyState || socket.readyState !== 1) {
        throw new Error('WebSocket is not open. Cannot send message.')
      }
      socket.send(payload)
    } else {
      console.log('🚫  Can not send to web socket')
    }
  }

  const sendMessageToWebsocket = async (payload: string) => {
    if (socketRef.current) {
      // console.log(`sending to websocket: ${JSON.parse(payload).action}`)
      //console.log(`sending to websocket: ${payload} connection state: ${connectionState}`)
      socketRef.current.send(payload)
    } else {
      console.log('🚫  Can not send to web socket.')
    }
  }


  function checkPaymentDetailsAndRedirect(redirectUrl: string) {
    const lastPaymentTimestamp = localStorage.getItem('lastPaymentRequestTimestamp');
    const currentTime = Date.now();
    // Check if the timestamp exists and is less than 2 minutes ago
    if (lastPaymentTimestamp) {
      const timeElapsed = currentTime - parseInt(lastPaymentTimestamp, 10);

      // If less than 10 minutes have passed, do nothing
      if (timeElapsed < 2 * 60 * 1000) {
        console.log('Payment details were requested less than 2 minutes ago. No redirect.');
        return;
      }
    }
    // If the timestamp doesn't exist or more than 2 minutes have passed, update the timestamp and redirect
    window.location.href = redirectUrl;
  }

  const mergeTableData = (oldTableData: ObjectStringKeyAnyValue, newData: ObjectStringKeyAnyValue) => {
    let mergedTableData: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      mergedTableData = { ...oldTableData };
      Object.keys(newData).forEach(key => {
        // Directly assigning the value from newData, overriding the oldData value if key exists in both
        mergedTableData[key] = newData[key];
      });
    } else {
      mergedTableData = { ...newData }
    }
    return mergedTableData
  }

  const setSingleItem = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    const resultTypeKey = Object.keys(payload)[0]
    const result = payload[resultTypeKey]
    const resultKey = Object.keys(result)[0]
    const resultValue = result[resultKey]
    console.log(`Setting single item ${resultTypeKey} ${result} ${resultKey} ${resultValue}`)
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[resultTypeKey] ||= {}
    newTableDataObj[resultTypeKey][resultKey] = resultValue
    return newTableDataObj
  }


  const setSingleItemFromNewRecord = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    const tableToUpdate = payload['tableName']
    const itemToUpdate = payload['keys'] ? payload['keys'][Object.keys(payload['keys'])[0]] : null
    const newValues = payload['newImage'] || payload['newRecord']
    //console.log(`Setting single item from new record ${tableToUpdate} ${itemToUpdate}`)
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[tableToUpdate] ||= {}


    // check for deleted or deactivated checklists
    const hasRecordBeenDeleted = newValues['isDeleted'] && newValues['isDeleted'] === 'Y'
    const hasChecklistBeenDisabled = newValues['isChecklistActive'] === false

    if (itemToUpdate) {
      if (hasRecordBeenDeleted || hasChecklistBeenDisabled) {
        //console.log(`🗑️  Deleting item ${itemToUpdate} from table ${tableToUpdate}`)
        delete newTableDataObj[tableToUpdate][itemToUpdate]
      } else {
        newTableDataObj[tableToUpdate][itemToUpdate] = newValues
      }
    } else {
      newTableDataObj[tableToUpdate] = newValues
    }
    return newTableDataObj
  }


  const setSingleTableData = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    const resultTypeKey = Object.keys(payload)[0]
    const result = payload[resultTypeKey]
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[resultTypeKey] ||= {}
    newTableDataObj[resultTypeKey] = result
    return newTableDataObj
  }

  const removeSingleItem = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    const tableNameToUpdate = payload['tableName']
    const itemToRemove = payload['keys'][Object.keys(payload['keys'])[0]]
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[tableNameToUpdate] ||= {}
    if (newTableDataObj[tableNameToUpdate] && newTableDataObj[tableNameToUpdate][itemToRemove]) {
      delete newTableDataObj[tableNameToUpdate][itemToRemove]
    }

    return newTableDataObj
  }


  const mergeSingleTableData = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    const resultTypeKey = Object.keys(payload)[0]
    const result = payload[resultTypeKey]
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[resultTypeKey] ||= {}
    for (const key in result) {
      newTableDataObj[resultTypeKey][key] = result[key]
    }
    return newTableDataObj
  }


  const fetchData = () => {
    let requestAssets = JSON.stringify({
      action: "checklistsClientApp",
      subAction: "fetchInitialData"
    })
    sendMessageToWebsocket(requestAssets)
  }

  const fetchAssetById = async (assetId: string) => {
    const existingAsset = tableData && tableData.Assets && tableData.Assets[assetId]
    if (!existingAsset && !assetIdsToFetch.includes(assetId)) {
      //console.log(`asset ${assetId} not found in table data. Fetching...`)
      // add it to the list of fetched assets
      const newAssetIdsToFetch = [...assetIdsToFetch]
      newAssetIdsToFetch.push(assetId)
      setAssetIdsToFetch(newAssetIdsToFetch)
      // send the request to fetch the asset
      const payload = JSON.stringify({
        action: "checklistsClientApp",
        subAction: "fetchAssetById",
        assetId: assetId
      })
      sendMessageToWebsocket(payload)
    }
  }




  const actOnMessage = () => {
    if (socket && socket.onmessage === null) {
      socket.onmessage = ({ data }: any) => {
        if (data) {
          const action = JSON.parse(data).action
          const payload = JSON.parse(data).payload
          publish(action, payload)
          //console.log(`📨 A message has been received: ${action}`)
          switch (action) {
            case 'error':
              console.log(`🚫  Error - action: ${action} and payload: ${payload && JSON.stringify(payload)}`)
              if (payload && payload.message) {
                setShowModal({ "error": ` ${payload.message}` })
              } else {
                if (payload && payload.message && payload.message === "No checklist period found with id") {
                  console.log("❎ No checklist period found with id - redirecting to home page")
                  window.location.href = '/'
                }
              }

              break

            case 'autherror':
              console.log('🚫  Authentication error - logging you out')
              handleLogout()
              break

            case 'loggedIn':
              // console.log("🪵 logged in")
              const loginDataBody = JSON.parse(data).body
              //console.log(loginDataBody)
              setConnectionState('connected-and-logged-in')
              setShowLoginModal(false)
              setLoggedIn(true)
              const rawUserData = loginDataBody.idTokenDecoded.payload
              setUserData(loginDataBody.idTokenDecoded.payload)
              setOrgSettings(loginDataBody.settings)
              setActiveFeatures(loginDataBody['activeFeatures'] || {})
              setExpiredFeatures(loginDataBody['expiredFeatures'] || {})

              const loggedInOrgId = loginDataBody.organisationId

              //console.log(`🐙 loggin in id: ${loggedInOrgId}  id from qr code: ${orgIdFromQrCodeRef.current}`)

              if (loggedInOrgId) {
                if (orgIdFromQrCodeRef.current && orgIdFromQrCodeRef.current !== loggedInOrgId) {
                  const allowedOrgIdsForThisUser = Object.keys(JSON.parse(rawUserData.organisationsForUser))
                  if (allowedOrgIdsForThisUser.includes(orgIdFromQrCodeRef.current)) {
                    console.log('✅ possible to switch to this org as the same user')
                    setOrgMismatchDialog('switchOrgAsSameUser')
                  } else {
                    if (loginOptionsRef.current && loginOptionsRef.current['Checklists'] && loginOptionsRef.current['Checklists'].includes('guest')) {
                      console.log('👤 possible to switch to this org as a guest user')
                      setOrgMismatchDialog('switchOrgAsGuest')
                    } else {
                      console.log('⛔️ not possible to switch to this org at all')
                      setOrgMismatchDialog('loginAsDifferentUser')
                    }
                  }
                }
                setCurrentOrganisation(loggedInOrgId)
                localStorage.setItem('selectedOrgId', loggedInOrgId)

              }
              break

            case 'refuseGuestConnection':
              console.log('⛔️ guest login refused')
              if (payload.showError) {
                setShowModal({ "error": ` ${payload.showError}` })
              }
              if (payload.showLog) {
                console.log(payload.showLog)
              }
              localStorage.removeItem('guestLogin')
              console.log('🚨')
              handleLogout()
              break

            case 'confirmGuestConnection':
              console.log('✅ guest login sucessful')
              setConnectionState('connected-and-logged-in')
              setLoggedIn('guest')
              setShowModal(null)
              // switch the org if it doesn't match the one in the payload
              if (payload.OrganisationId && payload.OrganisationId !== currentOrganisation) {
                switchCurrentOrganisation(payload.OrganisationId, false, false)
              }
              localStorage.setItem('guestLogin', JSON.stringify(payload))
              const userData = {
                "name": payload.UserName,
                organisationsForUser: JSON.stringify({
                  [payload.OrganisationId]: payload.OrganisationName
                })
              }
              setUserData(userData)
              break

            case 'confirmExternalUserConnection':
              console.log(`🔐 external user login successful: ${JSON.stringify(payload)}`)
              setLoggedIn('externalUser')
              setShowModal(null)
              const newExternalUserData: ObjectStringKeyAnyValue = {
                checklistId: payload.checklistId,
                GuestConnectionId: payload.Id,
                checklistPeriodId: payload.CurrentCheckPeriodId
              }

              setExternalUserState(newExternalUserData)
              localStorage.setItem('guestLogin', JSON.stringify(payload))

              if (payload.OrganisationId && payload.OrganisationId !== currentOrganisation) {
                switchCurrentOrganisation(payload.OrganisationId, false, false)
              }
              const externalUserData = {
                "name": payload.UserName,
                "Id": payload.Id,
                organisationsForUser: JSON.stringify({
                  [payload.OrganisationId]: payload.OrganisationName
                })
              }
              setConnectionState('connected-and-logged-in')
              // console.log('👤 external user data:')
              // console.log(externalUserData)
              setUserData(externalUserData)
              break


            case 'loginOptionsPermitted':
            case 'supportedLoginTypes':
              // if the login type is externalUser and we are logged in as a guest or email user then we
              // need to log out and reload the page
              const login_types = payload['loginOptions'] && Object.keys(payload['loginOptions'])

              if (login_types && login_types.includes('externalIndividual')) {
                // delete access token from local storage if it exists - 
                // this will log out users logged in by sso or email + password

                if (localStorage.getItem('localAccessToken')) {
                  console.log("Logging out to allow external individual login")
                  localStorage.removeItem('localAccessToken')
                  window.location.reload()
                }
                if (localStorage.getItem('guestLogin')) {
                  const guestLoginJson = localStorage.getItem('guestLogin')
                  const guestLoginObj = guestLoginJson ? JSON.parse(guestLoginJson) : null
                  // if the guest login does not have a key called 'checklist_id' then this is a guest user (not exernal user) and we need to log them out
                  if (guestLoginObj && !guestLoginObj.checklistId) {
                    localStorage.removeItem('guestLogin')
                    window.location.reload()
                  }
                }
              }

              //console.log(`✅ login options permitted: ${JSON.stringify(payload)}`)
              payload['organisationId'] && setOrgIdFromQrCode(payload['organisationId'])
              payload['loginOptions'] && setLoginOptions(payload['loginOptions'])

              break

            case 'loginFailed':
              handleLogout()
              break

            case 'loginOKButNoOrganisation':
              console.log("🚫  Login OK but no organisation")
              window.localStorage.setItem('loggedInNoOrgId', data)
              if (JSON.parse(data) && JSON.parse(data).idTokenDecoded) {
                setUserData(JSON.parse(data).idTokenDecoded.payload)
              }
              setLoggedIn(true)
              break

            case 'returnMultiTableData':
              //console.log(`❎ Multiple tables received`)
              //console.log(`❎ Multiple tables received: ${JSON.stringify(Object.keys(payload))}`)
              setTableData((oldTableData: ObjectStringKeyAnyValue) => mergeTableData(oldTableData, payload))
              break

            case 'returnSingleItem':
              setTableData((oldTableData: ObjectStringKeyAnyValue) => setSingleItem(oldTableData, payload))
              break

            case 'mergeSingleTableData':
              setTableData((oldTableData: ObjectStringKeyAnyValue) => mergeSingleTableData(oldTableData, payload))
              break

            case 'returnSingleTableData':
              setTableData((oldTableData: ObjectStringKeyAnyValue) => setSingleTableData(oldTableData, payload))
              break

            case 'tableUpdateItemAdded':
            case 'tableUpdateItemModified':
              setTableData((oldTableData: ObjectStringKeyAnyValue) => setSingleItemFromNewRecord(oldTableData, payload))
              break
            case 'tableUpdateItemRemoved':
              setTableData((oldTableData: ObjectStringKeyAnyValue) => removeSingleItem(oldTableData, payload))
              break

            case 'ok':
              //console.log('super admin command ok')
              break

            case 'agreementNeeded':
              setAgreementNeeded(payload)
              break;
            case 'agreementSaved':
              console.log(payload)
              setAgreementNeeded(null)
              break

            case 'paymentMethodNeeded':
              checkPaymentDetailsAndRedirect(payload['portalUrl'])
              break

            case 'returnPresignedUrl':
            case 'resetAnswerResponse':
              // handled when pub sub or when table data is updated
              break

            default:
              //console.log(`A message has been received but the action is invalid. ${data}`)
              break
          }
        }
      }
    }
  }


  if (window.location.pathname === '/loginreset') {
    console.log('🔥🔥🔥🔥🔥Resetting login state')
    setLoggedIn(false)
    setUserData(null)
    setAccessToken(null)
    setIdToken(null)
    setRefreshToken(null)
    localStorage.removeItem('lastVistedPage')
    localStorage.removeItem('localIdToken')
    localStorage.removeItem('localAccessToken')
    localStorage.removeItem('guestLogin')
    localStorage.removeItem('localRefreshToken')
    localStorage.removeItem('selectedOrgId')
    if (socket) {
      socket.close()
    }
    window.location.href = '/'
  }

  if (window.location.pathname === '/phonemode' || window.location.pathname === '/phonemode/') {
    localStorage.setItem('simulatedPhoneLogin', 'true')
    return <div className={`w-screen h-screen flex flex-col items-center justify-center`}>

      <div style={{
        borderTopLeftRadius: '30px',
        borderTopRightRadius: '30px',
        backgroundColor: '#000000',
        color: '#000000',
        width: '360px',
      }}>.</div>
      <iframe
        src={`${window.location.origin}`}
        style={{
          border: '10px solid black',
          width: '360px',
          height: '700px'
        }} />

      <div style={{
        borderBottomLeftRadius: '30px',
        borderBottomRightRadius: '30px',
        backgroundColor: '#000000',
        color: '#000000',
        width: '360px',
      }}>.</div>
      <a href='#' onClick={(event: any) => {
        event.preventDefault()
        localStorage.removeItem('simulatedPhoneLogin')
        window.location.href = '/'
      }}>Leave phone mode</a>
    </div>
  }


  const handleLogout = () => {
    console.log('🌍 Logging out...')
    localStorage.setItem('lastVistedPage', window.location.href)
    setLoggedIn(false)
    setUserData(null)
    setTableData(null)
    setAccessToken(null)
    setIdToken(null)
    setRefreshToken(null)
    localStorage.removeItem('localIdToken')
    localStorage.removeItem('localAccessToken')
    localStorage.removeItem('guestLogin')
    localStorage.removeItem('localRefreshToken')
    // close the websocket
    if (socket) {
      socket.close()
    }
  }

  const checkIfThereAreQuestionsAndAnswersForChecklistPeriodId = (checklistPeriodId: string) => {
    if (!questionsFetchedForCheckPeriodIds.includes(checklistPeriodId)) {
      const newQuestionsFetchedForCheckPeriodIds = [...questionsFetchedForCheckPeriodIds, checklistPeriodId]
      const payload = JSON.stringify({
        action: "checklistsClientApp",
        subAction: "fetchQuestionsAndAnswersForCheckPeriodId",
        checklistPeriodId: checklistPeriodId
      })
      //console.log(`🥎 fetching questions and answers for ${checklistPeriodId}`)
      sendMessageToWebsocketOrThrowException(payload).then(() => {
        //console.log("✅ sent message to web socket")
        setQuestionsFetchedForCheckPeriodIds(newQuestionsFetchedForCheckPeriodIds)
      }
      ).catch((error) => {
        console.log("❌ error sending message to web socket")
        console.log(error)
      })
    }
  }

  const fetchQuestionsAndAssetsForChecklistId = async (checklistId: string) => {
    //console.log(`🙉 fetching questions and assets for ${checklistId}`)
    if (!questionsAndAssetsFetchedForChecklists.includes(checklistId)) {
      //console.log('not already fetched questions and assets')
      const newQuestionsAndAssetsFetchedForChecklists = [...questionsAndAssetsFetchedForChecklists, checklistId]
      const payload = JSON.stringify({
        action: "checklistsClientApp",
        subAction: "fetchQuestionsAndAssetsForChecklistId",
        checklistId: checklistId
      })
      //console.log(`⏰⏰⏰ fetching assets for ${checklistId}`)
      sendMessageToWebsocketOrThrowException(payload).then(() => {
        //console.log("✅sent message to web socket")
        setQuestionsAndAssetsFetchedForChecklists(newQuestionsAndAssetsFetchedForChecklists)
      }).catch((error) => {
        // console.log("❌ error sending message to web socket")
        console.log(error)
      })
    } else {
      //console.log(`⏰⏰⏰⏰ already fetched assets for ${checklistId}`)
    }
  }


  const requestBackendAPIReading = async (jpegString: string, requestId: string, hints: null) => {
    // crop the jpeg string by taking 10% off each edge then scale down to 250x250 px
    const image = new Image();
    image.src = jpegString;
    await new Promise((resolve) => {
      image.onload = resolve;
    });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Failed to get canvas context');
      return;
    }
    const scaleFactor = 0.1; // 10% off each edge
    const scaledWidth = image.width * (1 - scaleFactor * 2);
    const scaledHeight = image.height * (1 - scaleFactor * 2);
    canvas.width = 250;
    canvas.height = 250;
    ctx.drawImage(image, image.width * scaleFactor, image.height * scaleFactor, scaledWidth, scaledHeight, 0, 0, 250, 250);
    const scaled_jpeg_string = canvas.toDataURL('image/jpeg', 0.8); // 80% quality
    const request = {
      action: "checklistsClientApp",
      subAction: 'requestAIReading',
      jpegString: scaled_jpeg_string,
      requestId: requestId,
      hints: hints
    }

    const payload = JSON.stringify(request)
    sendMessageToWebsocket(payload)
    // get rid of the canvas element to free up memory
    canvas.remove()

  }



  if (!loggedIn) {

    return <GlobalContext.Provider value={{
      tableData,
      setShowModal,
      userData,
      currentOrganisation,
      switchCurrentOrganisation,
      sendMessageToWebsocket,
      orgIdFromQrCode,
      processData,
      accessToken,
      idToken,
      fetchAssetById,
      connectionState,
      showLoginModal,
      setShowLoginModal,
      socket,
      loggedIn,
      getUserStats,
      t,
      isRTL
    }}>

      <VoiceProvider>
        {showLoginModal && <LoginModal />}
        {showModal && <MessageModal showModal={showModal} />}

        <Router>
          <Routes>

            <Route path='/checklist-qr-scanned/:checklistId'
              element={<NotLoggedIn
                loginOptions={loginOptions}
                showForm={'login'}
              />} />

            <Route path='/start-checklist/:checklistId'
              element={<NotLoggedIn
                loginOptions={loginOptions}
                showForm={'login'}
              />} />

            <Route path='/start-checklist-for-asset/:checklistId/:assetId'
              element={<NotLoggedIn
                loginOptions={loginOptions}
                showForm={'login'}
              />} />

            <Route path='/checklists/:checkPeriodId'
              element={<NotLoggedIn
                loginOptions={loginOptions}
                showForm={'login'}
              />} />

            <Route path='*'
              element={<NotLoggedIn
                loginOptions={loginOptions}
                showForm={'login'}
              />} />

            <Route path='/forgotPassword' element={<NotLoggedIn
              loginOptions={loginOptions}
              showForm={'forgotPassword'} />} />

          </Routes>
        </Router>
      </VoiceProvider>
    </GlobalContext.Provider>
  }

  if (loggedIn === 'externalUser') {

    return <div className={`flex flex-col bg-gradient-to-b from-tealgreen to-tealblue`}>
      <GlobalContext.Provider value={{
        checkIfThereAreQuestionsAndAnswersForChecklistPeriodId,
        fetchQuestionsAndAssetsForChecklistId,
        tableData,
        setShowModal,
        userData,
        currentOrganisation,
        isSignoffRequired,
        switchCurrentOrganisation,
        sendMessageToWebsocket,
        isVideoMode,
        setIsVideoMode,
        isVideoPlaying,
        setIsVideoPlaying,
        isInPhoneMode,
        setIsInPhoneMode,
        loggedIn,
        checkPeriodIdsForActiveCheckists,
        loginOptions,
        orgIdFromQrCode,
        handleLogout,
        processData,
        fetchAssetById,
        connectionState,
        requestBackendAPIReading,
        externalUserState,
        t,
        isRTL
      }}>
        <VoiceProvider>
          <OrientationProvider>
            <VideoProvider>
              {subscriptionExpired && <SubscriptionExpired />}

              {/* {subscriptionExpiringWithoutPayment && <SubscriptionExpiring
            setSubscriptionExpiringWithoutPayment={setSubscriptionExpiringWithoutPayment}
          />} */}
              <Router>


                <VideoBackground>



                  {showReconnectModal && <ReconnectModal />}
                  {showModal && <MessageModal showModal={showModal} />}


                  <Header
                    loggedIn={loggedIn}
                    setLoggedIn={setLoggedIn}
                    setUserData={setUserData}
                  />

                  <div
                    className={`z-0`}
                    style={{ padding: '60px 0 80px 0' }}
                  >



                    {agreementNeeded && <AppTermsDialog
                      details={agreementNeeded}
                      sendMessageToWebsocket={sendMessageToWebsocket}
                    />}


                    {!currentOrganisation && <NoOrganisation handleLogout={handleLogout} />}



                    {currentOrganisation && <Routes>



                      <Route path='/*'
                        element={<ExternalUserDashboard
                          externalUserState={externalUserState}
                          setExternalUserState={setExternalUserState}
                        />} />

                      <Route path='/checklist-qr-scanned/:checklistId'
                        element={<ChecklistCodeScanned />} />

                      <Route path='/start-checklist/:checklistId'
                        element={<StartAdhocChecklist
                          orgMismatchDialog={orgMismatchDialog}
                          setOrgMismatchDialog={setOrgMismatchDialog}
                        />} />

                      <Route path='/start-checklist-for-asset/:checklistId/:assetId'
                        element={<StartAdhocChecklist
                          orgMismatchDialog={orgMismatchDialog}
                          setOrgMismatchDialog={setOrgMismatchDialog}
                        />} />

                      <Route path='/checklists/:checkPeriodId'
                        element={<ChecklistQuestions />} />

                      <Route path='/checklists/:checkPeriodId/complete-checklist'
                        element={<CompleteChecklist />} />

                      <Route path='/checklists/:answerId/add-photo-note'
                        element={<AddPhotoNote />} />

                      <Route path='/checklists/:checkPeriodId/add-answer/:questionId'
                        element={<AddAnswerToQuestion />} />

                      <Route path='/checklists/:checkPeriodId/:answerId'
                        element={<QuestionPage showAnswerConfirmation={false} />} />

                      <Route path='/checklists/:checkPeriodId/confirm/:answerId'
                        element={<QuestionPage showAnswerConfirmation={true} />} />

                    </Routes>}

                  </div>
                </VideoBackground>
              </Router>
            </VideoProvider>
          </OrientationProvider>
        </VoiceProvider>
      </GlobalContext.Provider>


    </div>

  }

  return <div className={`flex flex-col bg-gradient-to-b from-tealgreen to-tealblue`}>
    <GlobalContext.Provider value={{
      checkIfThereAreQuestionsAndAnswersForChecklistPeriodId,
      fetchQuestionsAndAssetsForChecklistId,
      tableData,
      showModal,
      setShowModal,
      userData,
      currentOrganisation,
      isSignoffRequired,
      switchCurrentOrganisation,
      sendMessageToWebsocket,
      isVideoMode,
      setIsVideoMode,
      isVideoPlaying,
      setIsVideoPlaying,
      isInPhoneMode,
      setIsInPhoneMode,
      loggedIn,
      checkPeriodIdsForActiveCheckists,
      loginOptions,
      orgIdFromQrCode,
      handleLogout,
      processData,
      fetchAssetById,
      connectionState,
      requestBackendAPIReading,
      t,
      isRTL
    }}>
      <VoiceProvider>
        <OrientationProvider>
          <VideoProvider>
            {subscriptionExpired && <SubscriptionExpired />}

            <Router>


              <VideoBackground>



                {showReconnectModal && <ReconnectModal />}
                {showModal && <MessageModal showModal={showModal} />}


                <Header
                  loggedIn={loggedIn}
                  setLoggedIn={setLoggedIn}
                  setUserData={setUserData}
                />

                <div
                  className={`z-0`}
                  style={{ padding: '60px 0 80px 0' }}
                >



                  {agreementNeeded && <AppTermsDialog
                    details={agreementNeeded}
                    sendMessageToWebsocket={sendMessageToWebsocket}
                  />}


                  {!currentOrganisation && <NoOrganisation handleLogout={handleLogout} />}

                  {currentOrganisation && <Routes>

                    <Route path='/*'
                      element={<Dashboard />} />

                    <Route path='/checklists'
                      element={<AllChecklists />} />

                    <Route path='/processes/'
                      element={<ProcessList />} />

                    <Route path='/process/:processId'
                      element={<ProcessSummary />} />

                    <Route path='/start-checklist/:checklistId'
                      element={<StartAdhocChecklist
                        orgMismatchDialog={orgMismatchDialog}
                        setOrgMismatchDialog={setOrgMismatchDialog}
                      />} />

                    <Route path='/checklist-qr-scanned/:checklistId'
                      element={<ChecklistCodeScanned />} />

                    <Route path='/start-checklist-for-asset/:checklistId/:assetId'
                      element={<StartAdhocChecklist
                        orgMismatchDialog={orgMismatchDialog}
                        setOrgMismatchDialog={setOrgMismatchDialog}
                      />} />

                    <Route path='/checklists/:checkPeriodId'
                      element={<ChecklistQuestions />} />

                    <Route path='/scan-qr-code'
                      element={<ScanQrCode />} />

                    <Route path='/checklists/:checkPeriodId/scan-qr-code'
                      element={<ScanQrCode />} />

                    <Route path='/checklists/:checkPeriodId/complete-checklist'
                      element={<CompleteChecklist />} />

                    <Route path='/checklists/:answerId/add-photo-note'
                      element={<AddPhotoNote />} />

                    <Route path='/checklists/:checkPeriodId/add-answer/:questionId'
                      element={<AddAnswerToQuestion />} />

                    <Route path='/checklists/:checkPeriodId/:answerId'
                      element={<QuestionPage showAnswerConfirmation={false} />} />

                    <Route path='/checklists/:checkPeriodId/confirm/:answerId'
                      element={<QuestionPage showAnswerConfirmation={true} />} />




                  </Routes>}

                </div>
              </VideoBackground>
            </Router>
          </VideoProvider>
        </OrientationProvider>
      </VoiceProvider>
    </GlobalContext.Provider>


  </div>

}

export default App
